import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortOrder } from 'utils/array';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Card from 'components/UI/Card';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import PageWrapper from 'components/UI/PageWrapper';
import SearchField from 'components/UI/SearchField';
import Pagination, { PagePagination } from 'components/UI/Pagination';
import Loader from 'components/UI/Loader';
import { createRolloutFilterAttribute } from 'utils/filtering';
import { CardHeaderTop } from './styled';
import CardContainer from 'components/UI/CardContainer';
import RolloutTable from 'components/rollout/RolloutTable';
import { RolloutFilter, RolloutList } from 'models/rollout';
import {
  fetchRolloutList,
  getRollout,
  rolloutListSelector,
} from 'store/rolloutState/rolloutState';

// Initial pagination
const initialPagination = {
  limit: 10,
  offset: 0,
  page: 1,
};

const RolloutPage: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { data, isLoading, filters, totalCount }: RolloutList =
    useAppSelector(rolloutListSelector);

  useEffect(() => {
    if (!data) {
      dispatch(fetchRolloutList([], initialPagination.offset));
    }
  }, [dispatch, data]);

  // State
  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);

  // Fetch rollouts
  const fetchRollouts = useCallback(
    (rolloutFilters: RolloutFilter[], offset?: number) => {
      dispatch(fetchRolloutList(rolloutFilters, offset ?? 0));
    },
    [dispatch]
  );

  const fetchRolloutById = useCallback(
    (rolloutId: string) => {
      console.log(`fetchRolloutById ${rolloutId}`);
      dispatch(getRollout(rolloutId));
    },
    [dispatch]
  );

  // Search
  const onSearch = useCallback(
    (value: string) => {
      const searchFilters = createRolloutFilterAttribute(value);
      setPagination(initialPagination);
      if (searchFilters.length) {
        fetchRollouts(searchFilters, initialPagination.offset);
      }
    },
    [fetchRollouts]
  );

  const onClear = useCallback(() => {
    setPagination(initialPagination);
    fetchRollouts([], initialPagination.offset);
  }, [fetchRollouts]);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      if (filters.data) {
        fetchRollouts(filters.data, paginate.offset);
      } else {
        fetchRollouts([], paginate.offset);
      }
    },
    [fetchRollouts, filters.data]
  );

  // Polling
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);
  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const pollingCallback = () => {
      console.log('Polling...');

      const rolloutsToWaitFor = data?.filter(
        (item) =>
          item.attributes.status === 'starting' ||
          item.attributes.status === 'creating'
      );
      if (rolloutsToWaitFor && rolloutsToWaitFor.length > 0) {
        rolloutsToWaitFor?.forEach((rollout) => fetchRolloutById(rollout.id));
      }
      if (!rolloutsToWaitFor || rolloutsToWaitFor.length <= 0) {
        setIsPollingEnabled(false);
        console.log('No need to poll. Stopped polling.');
      }
    };

    const startPolling = () => {
      console.log('startPolling 30 sek');
      timerIdRef.current = setInterval(pollingCallback, 30000);
    };

    const stopPolling = () => {
      console.log('stopPolling');
      clearInterval(timerIdRef.current || undefined);
    };

    if (isPollingEnabled) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isPollingEnabled, data, fetchRolloutById]);

  // Render results
  const results = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!data || data.length === 0) {
      return null;
    }
    return (
      <CardContainer>
        <Card>
          <Heading type={HeadingType.h3} uppercase>
            <FormattedMessage id="rollout.table_title" />
          </Heading>
          <RolloutTable
            rollouts={data}
            initialActiveColumn={2}
            initialSortOrder={SortOrder.Asc}
          />
          <Pagination
            totalCount={totalCount || 0}
            noItems={data.length}
            pagination={pagination}
            setPagination={onSetPagination}
          />
        </Card>
      </CardContainer>
    );
  }, [data, isLoading, pagination, totalCount, onSetPagination]);

  return (
    <PageWrapper>
      <CardHeaderTop>
        <Heading>
          <FormattedMessage id="rollout.title" />
        </Heading>
      </CardHeaderTop>
      <Card>
        <CardHeaderTop></CardHeaderTop>
        <SearchField
          onSearch={onSearch}
          onClear={onClear}
          placeholder={intl.formatMessage({
            id: 'rollout.search_placeholder',
          })}
        ></SearchField>
      </Card>
      {results}
    </PageWrapper>
  );
};

export default RolloutPage;
